import { useQueryClient } from "@tanstack/react-query";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LogoutCurve, ProfileCircle } from "iconsax-react";
import { useNavigate } from "@tanstack/react-router";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLogout } from "@/api";
import { useAuthStore } from "@/stores/auth.store";
import { useSessionStore } from "@/stores/session.store";
import type { UserAccountPopoverProps } from "./UserAccountPopover.types";
import * as styles from "./UserAccountPopover.styles";

export type { UserAccountPopoverProps } from "./UserAccountPopover.types";

export const UserAccountPopover = memo(function UserAccountPopover({
  open,
  onClose,
}: UserAccountPopoverProps) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useAuthStore((s) => s.logout);
  const clearActiveStore = useSessionStore((s) => s.clearActiveStore);

  const iconSize = useMemo(() => parseFloat(String(theme.spacing(2.25))), [theme]);

  const { mutate: logoutMutate } = useLogout({
    mutation: {
      onSettled: () => {
        logout();
        clearActiveStore();
        queryClient.clear();
        void navigate({ to: "/login" });
      },
    },
  });

  const handleProfile = useCallback(() => {
    onClose();
    void navigate({ to: "/profile" });
  }, [navigate, onClose]);

  const handleLogout = useCallback(() => {
    onClose();
    logoutMutate();
  }, [logoutMutate, onClose]);

  if (!open) return null;

  return (
    <Box sx={styles.menu} role="menu" id="user-account-menu">
      <Box component="button" type="button" onClick={handleProfile} sx={styles.menuItem}>
        <Box sx={styles.menuItemIcon}>
          <ProfileCircle size={iconSize} variant="Linear" color="currentColor" />
        </Box>
        <Typography component="span" sx={styles.menuItemLabel}>
          {t("navbar.userMenu.profile")}
        </Typography>
      </Box>
      <Box component="button" type="button" onClick={handleLogout} sx={styles.logoutItem}>
        <Box sx={styles.logoutItemIcon}>
          <LogoutCurve size={iconSize} variant="Linear" color="currentColor" />
        </Box>
        <Typography component="span" sx={styles.logoutItemLabel}>
          {t("navbar.userMenu.logout")}
        </Typography>
      </Box>
    </Box>
  );
});
